import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  IconButton,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Spinner,
  Center,
  useToast,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  Checkbox,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiKey,
  FiPlus,
  FiMoreVertical,
  FiRotateCw,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiPause,
  FiPlay,
  FiRefreshCw,
} from 'react-icons/fi';
import { functionsUsEast1 } from '../../utils/init-firebase';
import AgentKeysPanel from '../../components/CRM/Admin/AgentKeysPanel';

/**
 * Origin Admin > API Keys (/crm/admin/api-keys)
 *
 * Two tabs:
 *  - Vendor Keys: company-level keys that authenticate the lead APIs
 *    (ingestLeadWebhookHTTP + lookupOriginUserHTTP) via `manageApiKeys`.
 *  - AI Agent Keys: per-user keys that authenticate the Origin Agent API
 *    (agentApiHTTP) via `manageAgentKeys` — see AgentKeysPanel.
 *
 * Both tables are service-role-only in Supabase. Keys are stored as SHA-256
 * hashes; the full key value is shown exactly once at create/rotate.
 */

const ENDPOINTS = [
  { label: 'Lead Ingestion', url: 'https://us-east1-future-operations-hub.cloudfunctions.net/ingestLeadWebhookHTTP' },
  { label: 'User Lookup', url: 'https://us-east1-future-operations-hub.cloudfunctions.net/lookupOriginUserHTTP' },
];

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ApiKeysPage() {
  const history = useHistory();
  const toast = useToast();

  // ---- theme values (all hooks before any early return) ----
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBG = useColorModeValue('white', 'gray.800');
  const subText = useColorModeValue('gray.600', 'gray.400');
  const tableHeadBG = useColorModeValue('gray.50', 'gray.700');
  const keyBoxBG = useColorModeValue('gray.100', 'gray.700');

  // ---- data state ----
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // ---- create/edit modal state ----
  const formModal = useDisclosure();
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [formKeyId, setFormKeyId] = useState(null);
  const [formLabel, setFormLabel] = useState('');
  const [formRateLimit, setFormRateLimit] = useState(100);
  const [formNotes, setFormNotes] = useState('');

  // ---- show-once modal state ----
  const showOnceModal = useDisclosure();
  const [newKey, setNewKey] = useState(null); // { value, label, rotated }
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  // ---- rotate dialog state ----
  const rotateDialog = useDisclosure();
  const [rotateTarget, setRotateTarget] = useState(null);
  const rotateCancelRef = useRef();

  // ---- delete dialog state ----
  const deleteDialog = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteCancelRef = useRef();

  const manageApiKeys = useMemo(() => httpsCallable(functionsUsEast1, 'manageApiKeys'), []);

  const loadKeys = useCallback(async () => {
    try {
      const res = await manageApiKeys({ action: 'list' });
      setKeys(res.data?.keys || []);
    } catch (err) {
      console.error('[ApiKeysPage] list error:', err);
      toast({
        title: 'Failed to load API keys',
        description: err.message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [manageApiKeys, toast]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  // ---- stats ----
  const stats = useMemo(() => {
    const active = keys.filter((k) => k.status === 'active').length;
    const totalRequests = keys.reduce((sum, k) => sum + (Number(k.usage_count) || 0), 0);
    const lastUsedTimes = keys.map((k) => (k.last_used_at ? new Date(k.last_used_at).getTime() : 0));
    const lastUsed = Math.max(0, ...lastUsedTimes);
    return {
      total: keys.length,
      active,
      totalRequests,
      lastUsed: lastUsed > 0 ? formatDateTime(new Date(lastUsed).toISOString()) : 'Never',
    };
  }, [keys]);

  // ---- create / edit ----
  const openCreate = () => {
    setFormMode('create');
    setFormKeyId(null);
    setFormLabel('');
    setFormRateLimit(100);
    setFormNotes('');
    formModal.onOpen();
  };

  const openEdit = (key) => {
    setFormMode('edit');
    setFormKeyId(key.id);
    setFormLabel(key.label || '');
    setFormRateLimit(key.rate_limit_per_hour || 100);
    setFormNotes(key.notes || '');
    formModal.onOpen();
  };

  const submitForm = async () => {
    const label = formLabel.trim();
    if (!label) {
      toast({ title: 'Label is required', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setBusy(true);
    try {
      if (formMode === 'create') {
        const res = await manageApiKeys({
          action: 'create',
          label,
          rateLimitPerHour: formRateLimit,
          notes: formNotes.trim() || null,
        });
        formModal.onClose();
        setNewKey({ value: res.data.fullKey, label, rotated: false });
        setSavedConfirmed(false);
        setCopied(false);
        showOnceModal.onOpen();
      } else {
        await manageApiKeys({
          action: 'update',
          id: formKeyId,
          label,
          rateLimitPerHour: formRateLimit,
          notes: formNotes.trim() || null,
        });
        formModal.onClose();
        toast({ title: 'API key updated', status: 'success', duration: 3000, isClosable: true });
      }
      await loadKeys();
    } catch (err) {
      console.error('[ApiKeysPage] save error:', err);
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  // ---- rotate ----
  const openRotate = (key) => {
    setRotateTarget(key);
    rotateDialog.onOpen();
  };

  const confirmRotate = async () => {
    if (!rotateTarget) return;
    setBusy(true);
    try {
      const res = await manageApiKeys({ action: 'rotate', id: rotateTarget.id });
      rotateDialog.onClose();
      setNewKey({ value: res.data.fullKey, label: rotateTarget.label, rotated: true });
      setSavedConfirmed(false);
      setCopied(false);
      showOnceModal.onOpen();
      await loadKeys();
    } catch (err) {
      console.error('[ApiKeysPage] rotate error:', err);
      toast({ title: 'Rotate failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
      setRotateTarget(null);
    }
  };

  // ---- enable / disable ----
  const toggleStatus = async (key) => {
    const nextStatus = key.status === 'active' ? 'disabled' : 'active';
    setBusy(true);
    try {
      await manageApiKeys({ action: 'update', id: key.id, status: nextStatus });
      toast({
        title: nextStatus === 'disabled' ? `"${key.label}" disabled` : `"${key.label}" enabled`,
        description: nextStatus === 'disabled' ? 'Requests with this key now receive 403 Invalid API key.' : 'This key can authenticate requests again.',
        status: nextStatus === 'disabled' ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
      });
      await loadKeys();
    } catch (err) {
      console.error('[ApiKeysPage] status toggle error:', err);
      toast({ title: 'Status change failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  // ---- delete ----
  const openDelete = (key) => {
    setDeleteTarget(key);
    setDeleteConfirmText('');
    deleteDialog.onOpen();
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.label) return;
    setBusy(true);
    try {
      await manageApiKeys({ action: 'delete', id: deleteTarget.id });
      deleteDialog.onClose();
      toast({ title: `"${deleteTarget.label}" deleted`, status: 'info', duration: 4000, isClosable: true });
      await loadKeys();
    } catch (err) {
      console.error('[ApiKeysPage] delete error:', err);
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  // ---- show-once helpers ----
  const copyNewKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey?.value || '');
      setCopied(true);
      toast({ title: 'Key copied to clipboard', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Copy failed — select and copy manually', status: 'warning', duration: 4000, isClosable: true });
    }
  };

  const closeShowOnce = () => {
    showOnceModal.onClose();
    setNewKey(null);
    setSavedConfirmed(false);
    setCopied(false);
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDir="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color={subText}>Loading API keys...</Text>
        </Center>
      </Container>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="100%" px={{ base: 4, md: 6, lg: 8 }} py={6}>
        <VStack align="stretch" spacing={6} maxW="1400px" mx="auto">
          {/* Header */}
          <Box bg={cardBG} p={6} borderRadius="xl" shadow="sm">
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3}>
                <Tooltip label="Back to CRM Admin">
                  <IconButton
                    icon={<FiArrowLeft />}
                    aria-label="Back to CRM Admin"
                    onClick={() => history.push('/crm/admin')}
                    size="lg"
                    variant="ghost"
                  />
                </Tooltip>
                <VStack align="flex-start" spacing={1}>
                  <HStack>
                    <FiKey />
                    <Heading size="lg">API Keys</Heading>
                  </HStack>
                  <Text color={subText} fontSize="sm">
                    Vendor keys for the lead APIs, and per-user AI agent keys for the Origin Agent API
                  </Text>
                </VStack>
              </HStack>
            </HStack>
          </Box>

          <Tabs colorScheme="blue" isLazy lazyBehavior="keepMounted">
            <TabList>
              <Tab>Vendor Keys</Tab>
              <Tab>AI Agent Keys</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={6}>
          {/* Vendor stats + actions */}
          <Box bg={cardBG} p={6} borderRadius="xl" shadow="sm">
            <HStack justify="space-between" align="flex-start" mb={4}>
              <VStack align="flex-start" spacing={1}>
                <Text fontWeight="bold" fontSize="lg">Vendor Keys</Text>
                <Text color={subText} fontSize="sm">
                  Company-level keys that authenticate the Origin lead APIs
                </Text>
              </VStack>
              <HStack>
                <Tooltip label="Refresh">
                  <IconButton icon={<FiRefreshCw />} aria-label="Refresh keys" variant="ghost" onClick={loadKeys} isDisabled={busy} />
                </Tooltip>
                <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate} isDisabled={busy}>
                  Create API Key
                </Button>
              </HStack>
            </HStack>

            {/* Stats */}
            <Grid templateColumns="repeat(4, 1fr)" gap={4}>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Total Keys</StatLabel>
                  <StatNumber>{stats.total}</StatNumber>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Active Keys</StatLabel>
                  <StatNumber color={stats.active > 0 ? 'green.500' : 'orange.500'}>{stats.active}</StatNumber>
                  <StatHelpText>{stats.total - stats.active} disabled</StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Total Requests</StatLabel>
                  <StatNumber>{stats.totalRequests.toLocaleString()}</StatNumber>
                  <StatHelpText>all-time, all keys</StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Last Request</StatLabel>
                  <StatNumber fontSize="md">{stats.lastUsed}</StatNumber>
                </Stat>
              </GridItem>
            </Grid>
          </Box>

          {/* Keys table */}
          <Box bg={cardBG} borderRadius="xl" shadow="sm" overflow="hidden">
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeadBG}>
                  <Tr>
                    <Th>Label</Th>
                    <Th>Key</Th>
                    <Th>Status</Th>
                    <Th isNumeric>Limit / hr</Th>
                    <Th>Created</Th>
                    <Th>Rotated</Th>
                    <Th>Last Used</Th>
                    <Th isNumeric>Requests</Th>
                    <Th width="1%" />
                  </Tr>
                </Thead>
                <Tbody>
                  {keys.length === 0 && (
                    <Tr>
                      <Td colSpan={9}>
                        <Center py={10} flexDir="column">
                          <Text color={subText} mb={3}>No API keys yet.</Text>
                          <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>
                            Create your first key
                          </Button>
                        </Center>
                      </Td>
                    </Tr>
                  )}
                  {keys.map((key) => (
                    <Tr key={key.id}>
                      <Td>
                        <VStack align="flex-start" spacing={0}>
                          <Text fontWeight="semibold">{key.label}</Text>
                          {key.notes && (
                            <Text fontSize="xs" color={subText} noOfLines={1} maxW="260px">
                              {key.notes}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Tooltip label="The full key is only shown once, at creation or rotation">
                          <Code fontSize="xs">{key.key_prefix}…</Code>
                        </Tooltip>
                      </Td>
                      <Td>
                        <Badge colorScheme={key.status === 'active' ? 'green' : 'red'}>{key.status}</Badge>
                      </Td>
                      <Td isNumeric>{key.rate_limit_per_hour}</Td>
                      <Td>
                        <VStack align="flex-start" spacing={0}>
                          <Text fontSize="xs">{formatDateTime(key.created_at)}</Text>
                          {key.created_by_name && (
                            <Text fontSize="xs" color={subText}>by {key.created_by_name}</Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Text fontSize="xs">{formatDateTime(key.rotated_at)}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs">{formatDateTime(key.last_used_at)}</Text>
                      </Td>
                      <Td isNumeric>{Number(key.usage_count || 0).toLocaleString()}</Td>
                      <Td>
                        <Menu placement="bottom-end">
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            aria-label={`Actions for ${key.label}`}
                            variant="ghost"
                            size="sm"
                            isDisabled={busy}
                          />
                          <MenuList>
                            <MenuItem icon={<FiRotateCw />} onClick={() => openRotate(key)}>
                              Rotate key
                            </MenuItem>
                            <MenuItem icon={<FiEdit2 />} onClick={() => openEdit(key)}>
                              Edit
                            </MenuItem>
                            <MenuItem
                              icon={key.status === 'active' ? <FiPause /> : <FiPlay />}
                              onClick={() => toggleStatus(key)}
                            >
                              {key.status === 'active' ? 'Disable' : 'Enable'}
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => openDelete(key)}>
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>

          {/* Endpoints info */}
          <Alert status="info" variant="left-accent" borderRadius="lg" alignItems="flex-start">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">These keys authenticate the Origin lead APIs</AlertTitle>
              <AlertDescription fontSize="sm">
                <VStack align="flex-start" spacing={1} mt={2}>
                  {ENDPOINTS.map((ep) => (
                    <HStack key={ep.url} spacing={2}>
                      <Badge>{ep.label}</Badge>
                      <Code fontSize="xs">POST {ep.url}</Code>
                    </HStack>
                  ))}
                  <Text pt={1}>
                    Vendors send the key via the <Code fontSize="xs">X-API-Key</Code> header. Full integration docs:{' '}
                    <Code fontSize="xs">WEBHOOK_API_DOCUMENTATION_FOR_VENDORS.md</Code>. Each key has its own hourly
                    rate-limit bucket per endpoint. Disabling or rotating a key takes effect immediately.
                  </Text>
                </VStack>
              </AlertDescription>
            </Box>
          </Alert>
                </VStack>
              </TabPanel>
              <TabPanel px={0}>
                <AgentKeysPanel />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>

      {/* Create / Edit modal */}
      <Modal isOpen={formModal.isOpen} onClose={formModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formMode === 'create' ? 'Create API Key' : `Edit "${formLabel}"`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Label</FormLabel>
                <Input
                  placeholder="e.g. Acme Leads"
                  value={formLabel}
                  onChange={(e) => setFormLabel(e.target.value)}
                />
                <FormHelperText>Who uses this key — usually the vendor or integration name.</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Rate limit (requests per hour, per endpoint)</FormLabel>
                <NumberInput
                  min={1}
                  max={100000}
                  value={formRateLimit}
                  onChange={(_, num) => setFormRateLimit(isNaN(num) ? 100 : num)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Default 100. Each endpoint (ingest, lookup) gets its own bucket.</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Notes (internal)</FormLabel>
                <Textarea
                  placeholder="Optional — contact person, contract details, etc."
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={formModal.onClose} isDisabled={busy}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={submitForm} isLoading={busy}>
              {formMode === 'create' ? 'Generate Key' : 'Save Changes'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Show-once modal */}
      <Modal
        isOpen={showOnceModal.isOpen}
        onClose={closeShowOnce}
        isCentered
        closeOnOverlayClick={false}
        closeOnEsc={false}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {newKey?.rotated ? `New secret for "${newKey?.label}"` : `API key for "${newKey?.label}"`}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box fontSize="sm">
                  <AlertTitle>This key is shown only once.</AlertTitle>
                  <AlertDescription>
                    Copy it now and deliver it to the vendor securely. It cannot be recovered — only rotated.
                    {newKey?.rotated && ' The previous key value has already stopped working.'}
                  </AlertDescription>
                </Box>
              </Alert>
              <HStack bg={keyBoxBG} p={3} borderRadius="md" justify="space-between">
                <Text fontFamily="mono" fontSize="sm" wordBreak="break-all" userSelect="all">
                  {newKey?.value}
                </Text>
                <Tooltip label={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton
                    icon={<FiCopy />}
                    aria-label="Copy key"
                    size="sm"
                    colorScheme={copied ? 'green' : 'blue'}
                    onClick={copyNewKey}
                  />
                </Tooltip>
              </HStack>
              <Checkbox isChecked={savedConfirmed} onChange={(e) => setSavedConfirmed(e.target.checked)}>
                I have copied this key and stored it somewhere safe
              </Checkbox>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={closeShowOnce} isDisabled={!savedConfirmed}>
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Rotate confirm dialog */}
      <AlertDialog
        isOpen={rotateDialog.isOpen}
        leastDestructiveRef={rotateCancelRef}
        onClose={rotateDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Rotate "{rotateTarget?.label}"?</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3} fontSize="sm">
                <Text>
                  A new secret is generated for this key and the current value{' '}
                  <Text as="span" fontWeight="bold">stops working immediately</Text>. The vendor must switch to the new
                  key before they can send requests again.
                </Text>
                <Text color={subText}>
                  Tip: for zero-downtime rotation, create a second key instead, move the vendor over, then delete this
                  one.
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={rotateCancelRef} variant="ghost" onClick={rotateDialog.onClose} isDisabled={busy}>
                Cancel
              </Button>
              <Button colorScheme="orange" onClick={confirmRotate} ml={3} isLoading={busy} leftIcon={<FiRotateCw />}>
                Rotate Key
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete confirm dialog */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={deleteCancelRef}
        onClose={deleteDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete "{deleteTarget?.label}"?</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3} fontSize="sm">
                <Text>
                  This permanently deletes the key. Any integration still using it will start receiving{' '}
                  <Code fontSize="xs">403 Invalid API key</Code>. This cannot be undone.
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">
                    Type <Text as="span" fontWeight="bold">{deleteTarget?.label}</Text> to confirm
                  </FormLabel>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={deleteTarget?.label}
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={deleteCancelRef} variant="ghost" onClick={deleteDialog.onClose} isDisabled={busy}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={busy}
                isDisabled={!deleteTarget || deleteConfirmText !== deleteTarget.label}
                leftIcon={<FiTrash2 />}
              >
                Delete Key
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
